import { useTranslation } from "react-i18next";
import { Image, Pressable } from "react-native";
import { Button, Text, TextInput } from "react-native-paper";
import { useAppTheme } from "../useAppTheme";
import AuthButton from "./AuthButton";
import Box from "./Box";
import MarkdownRenderer from "./MarkdownRenderer";
import ErrorView from "./RedeemCodeErrorView";

type Props = {
	title: string;
	subtitle?: string;
	logo: {
		uri: string;
	} | null;
	defaultCode: string;
	value: string;
	handleChangeText: (text: string) => void;
	handleSubmit: () => void;
	disableFab: boolean;
	loading: boolean;
	handleAskAssistant: () => void;
	email: string;
	showError: boolean;
	showOnboarding: string;
	handlePressContactSupport: () => void;
	errorMessage?: string;
	isCodeRedeemedBySameUser?: boolean;
	contactSupportTemplate: string;
	onNavigateToAlreadyRedeemedProduct: () => void;
	onSkipOnboarding: () => void;
};

const RedeemCodeView = ({
	title,
	subtitle,
	logo,
	defaultCode,
	value,
	handleChangeText,
	handleSubmit,
	disableFab,
	loading,
	handleAskAssistant,
	email,
	errorMessage,
	showError,
	showOnboarding,
	handlePressContactSupport,
	isCodeRedeemedBySameUser,
	contactSupportTemplate,
	onNavigateToAlreadyRedeemedProduct,
	onSkipOnboarding,
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();

	const cta = isCodeRedeemedBySameUser
		? t("redeemCode.access")
		: t("redeemCode.submit");

	return (
		<Box flex={1} gap={24} padding={16} width="100%">
			{logo && (
				<Image
					source={logo}
					resizeMode="contain"
					style={{
						width: "100%",
						height: 88,
						borderRadius: 20,
						alignSelf: "center",
					}}
				/>
			)}
			<Box>
				<Text
					variant="titleLarge"
					style={{
						color: theme.colors.text,
						textAlign: "center",
					}}
				>
					{title}
				</Text>
				{subtitle ? (
					<Text
						variant="bodyMedium"
						style={{
							color: theme.colors.onSurfaceVariant,
							textAlign: "center",
						}}
					>
						{subtitle}
					</Text>
				) : null}
			</Box>
			<Box>
				<TextInput
					placeholder="XXX-XXX-XXXXXXXX"
					mode="outlined"
					autoFocus
					defaultValue={defaultCode}
					value={value}
					onChangeText={handleChangeText}
					onKeyPress={(e) => {
						if (e.nativeEvent.key === "Enter") {
							handleSubmit();
						}
					}}
					disabled={loading}
				/>
				<ErrorView showError={showError} errorMessage={errorMessage} />
			</Box>
			<Button
				mode="contained"
				onPress={() => {
					if (isCodeRedeemedBySameUser) {
						onNavigateToAlreadyRedeemedProduct();
					} else {
						handleSubmit();
					}
				}}
				disabled={disableFab}
				loading={loading}
			>
				{cta}
			</Button>
			<Button mode="text" onPress={handleAskAssistant} disabled={!email}>
				{showError
					? t("redeemCode.troubleRedeeming")
					: t("redeemCode.helpFindCode")}
			</Button>
			{showError && (
				<Pressable
					onPress={handlePressContactSupport}
					style={{
						paddingBottom: 8,
					}}
				>
					<MarkdownRenderer
						variant="explanation"
						textAlign="center"
						text={contactSupportTemplate}
						overrideRules={{
							// biome-ignore lint/suspicious/noExplicitAny: Valid use case
							strong: (node: any, children: any) => (
								<Text key={node.key} variant="titleSmall">
									{children}
								</Text>
							),
						}}
					/>
				</Pressable>
			)}
			{showOnboarding && (
				<AuthButton onPress={onSkipOnboarding} mode="text" disabled={loading}>
					{t("redeemCode.skipStep")}
				</AuthButton>
			)}
		</Box>
	);
};

export default RedeemCodeView;
